import { ChangeDetectionStrategy, Component, Inject, OnDestroy } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { Actions, ofType } from '@ngrx/effects';
import { select, Store } from '@ngrx/store';
import { Subject } from 'rxjs';
import { filter, map, takeUntil } from 'rxjs/operators';
import { AppConfigService } from 'src/app/app-config/app-config-service';
import {
   deleteCallRecording,
   deleteCallRecordingSuccess,
} from 'src/app/pages/call-logs-register/state/call-logs-register.actions';
import { FileOption } from 'src/app/pages/prompt-details/models/file.option.interface';
import { getFileUrlFromToken, isFileUrl } from 'src/app/pipes/file-url-pipe/file-url';
import { getCallRecordingsPrivileges } from 'src/app/privileges/privileges.selector';

@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    selector: 'ndt-audio-player',
    styleUrls: ['./audio-player.component.scss'],
    templateUrl: './audio-player.component.html',
    standalone: false
})
export class AudioPlayerComponent implements OnDestroy {
   title = 'Audio Player';

   files: Array<{ title: string; file: FileOption }> = [];

   fileNotFound = false;

   canDeleteCallRecording: boolean;

   callRecordingId: number | null;

   private destroyed$ = new Subject<void>();

   constructor(
      private store: Store,
      private actions$: Actions,
      private dialogRef: MatDialogRef<AudioPlayerComponent>,
      private appConfig: AppConfigService,
      @Inject(MAT_DIALOG_DATA) private data: AudioPlayerData
   ) {
      data.files.forEach((item) => {
         this.files.push(item);
      });
      this.callRecordingId = data.callRecordingId;

      this.store
         .pipe(
            select(getCallRecordingsPrivileges),
            takeUntil(this.destroyed$),
            map((privileges) => {
               this.canDeleteCallRecording = privileges.canDelete;
            })
         )
         .subscribe();

      this.actions$
         .pipe(
            ofType(deleteCallRecordingSuccess),
            filter((action) => action.callRecordingId === this.callRecordingId),
            takeUntil(this.destroyed$)
         )
         .subscribe(() => {
            this.dialogRef.close();
         });
   }
   ngOnDestroy(): void {
      this.destroyed$.next();
   }

   onCloseClick(): void {
      this.dialogRef.close();
   }

   onDeleteClick(): void {
      if (this.callRecordingId) {
         const callRecordingToDelete: number = this.callRecordingId;
         this.store.dispatch(deleteCallRecording({ callRecordingId: callRecordingToDelete }));
      }
   }

   onFileNotFound() {
      this.fileNotFound = true;
   }

   getExtensionFromMime(type: string): string {
      const map: Record<string, string> = {
         'audio/wav': 'wav',
         'audio/x-wav': 'wav',
         'audio/mpeg': 'mp3',
         'audio/mp3': 'mp3',
         'audio/ogg': 'ogg',
         'audio/aac': 'aac',
      };

      return map[type] || '';
   }

   downloadRecording(file) {
      const url = this.buildUrl(file.fileToken);

      fetch(url, { credentials: 'include' })
         .then((res) => res.blob().then((blob) => ({ blob, type: res.headers.get('Content-Type') || file.mimeType })))
         .then(({ blob, type }) => {
            const ext = this.getExtensionFromMime(type);
            const filename = `${file.displayName || 'recording'}${ext ? '.' + ext : ''}`;

            const blobUrl = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = blobUrl;
            a.download = filename;
            a.click();
            URL.revokeObjectURL(blobUrl);
         });
   }

   private buildUrl(value: string): string {
      const filereaderUrl = `${this.appConfig.getConfig().filereaderUrl}`;
      if (isFileUrl(value, filereaderUrl)) {
         return value;
      }

      return `${filereaderUrl}${getFileUrlFromToken(value as string)}`;
   }
}
export interface AudioPlayerData {
   files: Array<{ title: string; file: FileOption }>;
   callRecordingId: number | null;
}
